"use client";
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Area,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  ComposedChart,
  ReferenceArea,
  ResponsiveContainer,
} from "recharts";
import { Button } from "../chartUi/button";
import { Card, CardContent, CardHeader, CardTitle } from "../chartUi/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from "../chartUi/chart";

import { DictionaryProps } from "@/types";

import * as S from "./styles";

type DataPoint = Record<string, any>;

type ZoomableChartProps = {
  data?: DataPoint[];
  titleChart: string;
  xAxisDataKey: string;
  yAxisDataKey: string | string[];
  xAxisLabel?: string;
  yAxisLabel?: string;
  tooltipNameKey?: string;
  dict?: DictionaryProps["dict"];
  chartConfig: ChartConfig;
  barColors?: string[];
  showLegendIcon?: boolean;
};

const seedRandom = (seed: number) => {
  const x = Math.sin(seed++) * 10000;
  return x - Math.floor(x);
};

export function simulateData(
  start = "2024-01-01T00:00:00Z",
  end = "2024-01-02T00:00:00Z",
): DataPoint[] {
  const simulatedData = [];
  let baseValue = 50;
  for (
    let currentDate = new Date(start);
    currentDate <= new Date(end);
    currentDate.setTime(currentDate.getTime() + 600000)
  ) {
    const seed = currentDate.getTime();
    baseValue = Math.max(
      (baseValue +
        ((0.5 * (currentDate.getTime() - new Date(start).getTime())) /
          (new Date(end).getTime() - new Date(start).getTime())) *
          100 +
        (seedRandom(seed) - 0.5) * 20 +
        (seedRandom(seed + 1) < 0.1 ? (seedRandom(seed + 2) - 0.5) * 50 : 0) +
        Math.sin(currentDate.getTime() / 3600000) * 10) *
        (1 + (seedRandom(seed + 3) - 0.5) * 0.2),
      1,
    );
    simulatedData.push({
      date: currentDate.toISOString(),
      events: Math.max(Math.floor(baseValue), 1),
    });
  }
  return simulatedData;
}

export function ZoomableChart({
  data: initialData,
  titleChart,
  xAxisDataKey,
  yAxisDataKey,
  xAxisLabel,
  yAxisLabel,
  tooltipNameKey,
  dict,
  chartConfig,
  barColors,
  showLegendIcon,
}: ZoomableChartProps) {
  const [data, setData] = useState<DataPoint[]>(initialData || []);
  const [refAreaLeft, setRefAreaLeft] = useState<string | null>(null);
  const [refAreaRight, setRefAreaRight] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<string | null>(null);
  const [endTime, setEndTime] = useState<string | null>(null);
  const [originalData, setOriginalData] = useState<DataPoint[]>(
    initialData || [],
  );
  const [isSelecting, setIsSelecting] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);
  const instanceId = useRef(Math.random().toString(36).slice(2, 9));

  //const color = chartConfig[yAxisDataKey]?.color
  const color = !Array.isArray(yAxisDataKey)
    ? chartConfig[yAxisDataKey]?.color
    : undefined;

  useEffect(() => {
    if (initialData?.length) {
      setData(initialData);
      setOriginalData(initialData);
      setStartTime(initialData[0]?.[xAxisDataKey]);
      setEndTime(initialData[initialData.length - 1]?.[xAxisDataKey]);
    }
  }, [initialData, xAxisDataKey]);

  const zoomedData = useMemo(() => {
    if (!startTime || !endTime || !xAxisDataKey) return data;

    // Testa se o eixo X é data
    const isDate = (val: any) => {
      const d = new Date(val);
      return !isNaN(d.getTime());
    };

    if (isDate(startTime) && isDate(endTime)) {
      // Filtro por timestamp para datas
      const start = new Date(startTime).getTime();
      const end = new Date(endTime).getTime();
      const filtered = originalData.filter((dataPoint) => {
        const curr = new Date(dataPoint[xAxisDataKey]).getTime();
        return curr >= start && curr <= end;
      });
      return filtered.length > 1 ? filtered : originalData.slice(0, 2);
    } else {
      // Filtro por índice para categorias
      const startIdx = originalData.findIndex(
        (d) => d[xAxisDataKey] === startTime,
      );
      const endIdx = originalData.findIndex((d) => d[xAxisDataKey] === endTime);
      if (startIdx !== -1 && endIdx !== -1) {
        const [from, to] = [startIdx, endIdx].sort((a, b) => a - b);
        const filtered = originalData.slice(from, to + 1);
        return filtered.length > 1 ? filtered : originalData.slice(0, 2);
      }
      return originalData;
    }
  }, [startTime, endTime, originalData, data, xAxisDataKey]);

  // const total = useMemo(
  //     // Calcular o total usando a chave dinâmica do eixo Y
  //     () => zoomedData.reduce((acc, curr) => acc + (Number(curr[yAxisDataKey]) || 0), 0),
  //     [zoomedData, yAxisDataKey]
  // );

  const total = useMemo(() => {
    if (Array.isArray(yAxisDataKey)) {
      return yAxisDataKey.reduce((totalAcc, key) => {
        return (
          totalAcc +
          zoomedData.reduce((acc, curr) => acc + (Number(curr[key]) || 0), 0)
        );
      }, 0);
    } else {
      return zoomedData.reduce(
        (acc, curr) => acc + (Number(curr[yAxisDataKey]) || 0),
        0,
      );
    }
  }, [zoomedData, yAxisDataKey]);

  // Calcula o maior valor entre as séries exibidas para ajustar o domínio do eixo Y
  const maxY = useMemo(() => {
    if (!zoomedData || !zoomedData.length) return 0;
    const keys = Array.isArray(yAxisDataKey) ? yAxisDataKey : [yAxisDataKey];
    let max = 0;
    for (const row of zoomedData) {
      for (const k of keys) {
        const v = Number(row[k]) || 0;
        if (v > max) max = v;
      }
    }
    return max;
  }, [zoomedData, yAxisDataKey]);

  const handleMouseDown = (e: any) => {
    if (e.activeLabel) {
      setRefAreaLeft(e.activeLabel);
      setIsSelecting(true);
    }
  };

  const handleMouseMove = (e: any) => {
    if (isSelecting && e.activeLabel) {
      setRefAreaRight(e.activeLabel);
    }
  };

  const handleMouseUp = () => {
    if (refAreaLeft && refAreaRight) {
      const [left, right] = [refAreaLeft, refAreaRight].sort();
      setStartTime(left);
      setEndTime(right);
    }
    setRefAreaLeft(null);
    setRefAreaRight(null);
    ChartTooltip;
    setIsSelecting(false);
  };

  const handleReset = () => {
    if (originalData.length > 0) {
      setStartTime(originalData[0]?.[xAxisDataKey]);
      setEndTime(originalData[originalData.length - 1]?.[xAxisDataKey]);
    }
  };

  const isDateAxis = (value: any) => {
    const date = new Date(value);
    return !isNaN(date.getTime());
  };

  const handleZoom = (
    e: React.WheelEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  ) => {
    //e.preventDefault();

    if (
      !originalData.length ||
      !chartRef.current ||
      typeof startTime !== "string" ||
      typeof endTime !== "string"
    ) {
      console.warn(
        "Zooming requires date-like xAxisDataKey and valid start/end times.",
      );
      return;
    }

    // Só permite zoom se o eixo X for data
    if (!isDateAxis(startTime) || !isDateAxis(endTime)) {
      // Para categorias, desabilita zoom
      return;
    }

    const startDate = new Date(startTime);
    const endDate = new Date(endTime);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      console.warn("StartTime or EndTime is not a valid date. Zoom disabled.");
      return;
    }

    const zoomFactor = 0.1;
    let direction = 0;
    let clientX = 0;

    if ("deltaY" in e) {
      // Evento roda do mouse
      direction = e.deltaY < 0 ? 1 : -1;
      clientX = e.clientX;
    } else if ("touches" in e && e.touches.length === 2) {
      // Pinch zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const currentDistance = Math.hypot(
        touch1.clientX - touch2.clientX,
        touch1.clientY - touch2.clientY,
      );

      if ((e as any).lastTouchDistance) {
        direction = currentDistance > (e as any).lastTouchDistance ? 1 : -1;
      }
      (e as any).lastTouchDistance = currentDistance;

      clientX = (touch1.clientX + touch2.clientX) / 2;
    } else {
      // Evento não suportado para zoom
      return;
    }

    const currentRange = endDate.getTime() - startDate.getTime();
    const zoomAmount = currentRange * zoomFactor * direction;

    const chartRect = chartRef.current.getBoundingClientRect();
    const mouseX = clientX - chartRect.left;
    const chartWidth = chartRect.width;
    const mousePercentage = mouseX / chartWidth;

    const newStartTime = new Date(
      startDate.getTime() + zoomAmount * mousePercentage,
    );
    const newEndTime = new Date(
      endDate.getTime() - zoomAmount * (1 - mousePercentage),
    );

    // Evita intervalo invertido ou muito pequeno
    if (newEndTime <= newStartTime) {
      return;
    }

    setStartTime(newStartTime.toISOString());
    setEndTime(newEndTime.toISOString());
  };

  useEffect(() => {
    const chartDiv = chartRef.current;
    if (!chartDiv) return;

    const wheelHandler = (e: WheelEvent) => {
      e.preventDefault();
      handleZoom(e as any);
    };

    chartDiv.addEventListener("wheel", wheelHandler, { passive: false });

    return () => {
      chartDiv.removeEventListener("wheel", wheelHandler);
    };
  }, [handleZoom]);

  const formatXAxis = (tickItem: any) => {
    if (
      typeof tickItem === "string" &&
      (tickItem.includes("T") || tickItem.includes("-"))
    ) {
      const date = new Date(tickItem);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString();
      }
    }
    return String(tickItem);
  };

  return (
    <S.CardChart className="w-full h-full">
      <S.ChartHeader className="flex-col items-stretch space-y-0 border-b p-0 sm:flex-row hidden sm:flex">
        <div className="flex flex-1 flex-col justify-center gap-1">
          <CardTitle>{titleChart}</CardTitle>
        </div>
        {/* <div className="flex">
                    <div className="relative z-30 flex flex-1 flex-col justify-center gap-1 text-left">
                        <span className="text-xs text-muted-foreground">
                            {dict?.Total}
                        </span>
                        <span className="text-lg font-bold leading-none sm:text-3xl">
                            {total.toLocaleString()}
                        </span>
                    </div>
                </div> */}
      </S.ChartHeader>
      <S.ChartContainer className="px-2 sm:p-6 h-full sm:h-[calc(100%-150px)]">
        <ChartContainer config={chartConfig} className="w-full h-full">
          <div
            className="h-full"
            onTouchMove={handleZoom}
            ref={chartRef}
            style={{ touchAction: "none" }}
          >
            {/* <div className="flex justify-end my-2 sm:mb-4">
                            <Button variant="outline" onClick={handleReset} disabled={!startTime && !endTime} className="text-xs sm:text-sm">
                                Reset
                            </Button>
                        </div> */}
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={zoomedData}
                margin={{
                  top: 10,
                  right: 10,
                  left: 0,
                  bottom: 0,
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <defs>
                  <linearGradient id="colorMain" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color} stopOpacity={0.8} />
                    <stop offset="95%" stopColor={color} stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey={xAxisDataKey}
                  tickFormatter={formatXAxis}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={4}
                  minTickGap={16}
                  style={{
                    fontSize: "10px",
                    userSelect: "none",
                  }}
                  // label={{
                  //     value: xAxisLabel,
                  //     position: "insideBottomCenter",
                  //     dy: 25,
                  //     style: { textAnchor: "middle", fontWeight: "bold" },
                  // }}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  style={{
                    fontSize: "10px",
                    userSelect: "none",
                  }}
                  width={50}
                  label={{
                    angle: -90,
                    position: "insideLeft",
                    fontSize: 12,
                  }}
                  tick={{ fill: "rgba(100,100,100,0.9)" }}
                  tickFormatter={(val) => Number(val).toLocaleString()}
                  domain={[0, maxY > 0 ? "dataMax" : 1]}
                  tickCount={5}
                />
                <ChartTooltip
                  cursor={false}
                  content={
                    <ChartTooltipContent
                      className="w-[150px] sm:w-[200px] font-mono text-xs sm:text-sm"
                      nameKey={tooltipNameKey}
                      labelFormatter={(value) => {
                        // Se for data, formata. Senão, só retorna o valor.
                        if (
                          typeof value === "string" &&
                          (value.includes("T") || value.includes("-"))
                        ) {
                          const date = new Date(value);
                          if (!isNaN(date.getTime())) {
                            return date.toLocaleString();
                          }
                        }
                        return String(value);
                      }}
                      formatter={(value: any, name: any) => [
                        `${tooltipNameKey || name}: `,
                        value,
                      ]}
                    />
                  }
                />
                <ChartLegend
                  content={<ChartLegendContent hideIcon={showLegendIcon} />}
                />
                {/* <Bar
                                    type="monotone"
                                    dataKey={yAxisDataKey}
                                    stroke="none"
                                    fillOpacity={1}
                                    fill={color}
                                    isAnimationActive={false}
                                >
                                {zoomedData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={barColors?.[index] || color} />
                                ))}
                                </Bar> */}
                {Array.isArray(yAxisDataKey)
                  ? yAxisDataKey.map((key, idx) => {
                      const seriesColor =
                        chartConfig[key]?.color ||
                        `hsl(var(--accent-${idx + 1}))`;
                      const gradientId = `grad-${key}-${idx}-${instanceId.current}`;
                      return (
                        <React.Fragment key={`series-${key}-${idx}`}>
                          <defs>
                            <linearGradient
                              id={gradientId}
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="5%"
                                stopColor={seriesColor}
                                stopOpacity={0.6}
                              />
                              <stop
                                offset="95%"
                                stopColor={seriesColor}
                                stopOpacity={0.05}
                              />
                            </linearGradient>
                          </defs>
                          <Area
                            key={`area-${key}`}
                            type="monotone"
                            dataKey={key}
                            stroke={seriesColor}
                            fill={`url(#${gradientId})`}
                            strokeWidth={3}
                            fillOpacity={0.25}
                            dot={false}
                            isAnimationActive={false}
                          />
                          <Line
                            key={`line-${key}`}
                            type="monotone"
                            dataKey={key}
                            stroke={seriesColor}
                            strokeWidth={3}
                            dot={{ r: 3 }}
                            isAnimationActive={false}
                          />
                        </React.Fragment>
                      );
                    })
                  : (() => {
                      const key = yAxisDataKey as string;
                      const seriesColor =
                        chartConfig[key]?.color ||
                        color ||
                        "hsl(var(--accent-1))";
                      const gradientId = `grad-${key}-single-${instanceId.current}`;
                      return (
                        <React.Fragment>
                          <defs>
                            <linearGradient
                              id={gradientId}
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="5%"
                                stopColor={seriesColor}
                                stopOpacity={0.6}
                              />
                              <stop
                                offset="95%"
                                stopColor={seriesColor}
                                stopOpacity={0.05}
                              />
                            </linearGradient>
                          </defs>
                          <Area
                            key={`area-${key}`}
                            type="monotone"
                            dataKey={key}
                            stroke={seriesColor}
                            fill={`url(#${gradientId})`}
                            strokeWidth={2}
                            dot={false}
                            fillOpacity={0.25}
                            isAnimationActive={false}
                          />
                          <Line
                            key={`line-${key}`}
                            type="monotone"
                            dataKey={key}
                            stroke={seriesColor}
                            strokeWidth={3}
                            dot={{ r: 3, stroke: "#fff", strokeWidth: 1 }}
                            isAnimationActive={false}
                          />
                        </React.Fragment>
                      );
                    })()}
                {refAreaLeft && refAreaRight && (
                  <ReferenceArea
                    x1={refAreaLeft}
                    x2={refAreaRight}
                    strokeOpacity={0.3}
                    fill="hsl(var(--foreground))"
                    fillOpacity={0.05}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </ChartContainer>
      </S.ChartContainer>
    </S.CardChart>
  );
}
